import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  adminService,
  type SitePageCardRow,
  type SitePageCardSectionRow,
} from "@/project/jain_elibrary-admin/services/adminService";
import { Loader2, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type PageSlug = "committee" | "contact";

type Props = {
  pageSlug: PageSlug;
  heading: string;
};

const emptySection = (
  pageSlug: PageSlug
): Omit<
  SitePageCardSectionRow,
  "id" | "cards" | "created_on" | "updated_on"
> => ({
  page_slug: pageSlug,
  title: "",
  sort_order: 0,
  grid_columns: 3,
  is_active: true,
});

const emptyCard = (
  sectionId: number
): Omit<SitePageCardRow, "id" | "created_on" | "updated_on"> => ({
  section: sectionId,
  sort_order: 0,
  name: "",
  email: "",
  phone: "",
  role: "",
  detail_line: "",
  is_active: true,
});

export default function SitePageCardsAdmin({ pageSlug, heading }: Props) {
  const [sections, setSections] = useState<SitePageCardSectionRow[]>([]);
  const [pageTitle, setPageTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingTitle, setSavingTitle] = useState(false);

  const [sectionDialog, setSectionDialog] = useState(false);
  const [editingSection, setEditingSection] =
    useState<SitePageCardSectionRow | null>(null);
  const [sectionForm, setSectionForm] = useState(emptySection(pageSlug));
  const [savingSection, setSavingSection] = useState(false);

  const [cardDialog, setCardDialog] = useState(false);
  const [cardSectionId, setCardSectionId] = useState<number | null>(null);
  const [editingCard, setEditingCard] = useState<SitePageCardRow | null>(null);
  const [cardForm, setCardForm] = useState(emptyCard(0));
  const [savingCard, setSavingCard] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [secs, pageMeta] = await Promise.all([
        adminService.getSitePageSections(pageSlug),
        adminService.getSiteContentPage(pageSlug).catch(() => null),
      ]);
      setSections(secs);
      if (pageMeta?.title) {
        setPageTitle(pageMeta.title);
      }
    } catch (e: unknown) {
      console.error(e);
      toast.error("Could not load sections.");
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [pageSlug]);

  useEffect(() => {
    void load();
  }, [load]);

  const savePageTitle = async () => {
    try {
      setSavingTitle(true);
      await adminService.patchSiteContentPage(pageSlug, {
        title: pageTitle.trim(),
      });
      toast.success("Page title saved.");
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail ?? "Save failed.");
    } finally {
      setSavingTitle(false);
    }
  };

  const openCreateSection = () => {
    setEditingSection(null);
    setSectionForm(emptySection(pageSlug));
    setSectionDialog(true);
  };

  const openEditSection = (s: SitePageCardSectionRow) => {
    setEditingSection(s);
    setSectionForm({
      page_slug: s.page_slug,
      title: s.title,
      sort_order: s.sort_order,
      grid_columns: s.grid_columns,
      is_active: s.is_active,
    });
    setSectionDialog(true);
  };

  const saveSection = async () => {
    if (!sectionForm.title.trim()) {
      toast.error("Section title is required.");
      return;
    }
    try {
      setSavingSection(true);
      if (editingSection) {
        await adminService.patchSitePageSection(editingSection.id, {
          title: sectionForm.title.trim(),
          sort_order: sectionForm.sort_order,
          grid_columns: sectionForm.grid_columns,
          is_active: sectionForm.is_active,
        });
        toast.success("Section updated.");
      } else {
        await adminService.createSitePageSection({
          page_slug: pageSlug,
          title: sectionForm.title.trim(),
          sort_order: sectionForm.sort_order,
          grid_columns: sectionForm.grid_columns,
          is_active: sectionForm.is_active,
        });
        toast.success("Section created.");
      }
      setSectionDialog(false);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: unknown } };
      toast.error("Could not save section.");
      console.error(err);
    } finally {
      setSavingSection(false);
    }
  };

  const removeSection = async (s: SitePageCardSectionRow) => {
    if (
      !window.confirm(
        `Delete section "${s.title}" and all its cards? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await adminService.deleteSitePageSection(s.id);
      toast.success("Section deleted.");
      await load();
    } catch (e: unknown) {
      console.error(e);
      toast.error("Delete failed.");
    }
  };

  const openCreateCard = (sectionId: number) => {
    setEditingCard(null);
    setCardSectionId(sectionId);
    setCardForm(emptyCard(sectionId));
    setCardDialog(true);
  };

  const openEditCard = (c: SitePageCardRow) => {
    setEditingCard(c);
    setCardSectionId(c.section);
    setCardForm({
      section: c.section,
      sort_order: c.sort_order,
      name: c.name,
      email: c.email ?? "",
      phone: c.phone ?? "",
      role: c.role ?? "",
      detail_line: c.detail_line ?? "",
      is_active: c.is_active,
    });
    setCardDialog(true);
  };

  const saveCard = async () => {
    if (!cardForm.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    const sid = cardSectionId ?? cardForm.section;
    if (!sid) {
      toast.error("Invalid section.");
      return;
    }
    try {
      setSavingCard(true);
      if (editingCard) {
        await adminService.patchSitePageCard(editingCard.id, {
          sort_order: cardForm.sort_order,
          name: cardForm.name.trim(),
          email: cardForm.email.trim(),
          phone: cardForm.phone.trim(),
          role: cardForm.role.trim(),
          detail_line: cardForm.detail_line.trim(),
          is_active: cardForm.is_active,
        });
        toast.success("Card updated.");
      } else {
        await adminService.createSitePageCard({
          section: sid,
          sort_order: cardForm.sort_order,
          name: cardForm.name.trim(),
          email: cardForm.email.trim(),
          phone: cardForm.phone.trim(),
          role: cardForm.role.trim(),
          detail_line: cardForm.detail_line.trim(),
          is_active: cardForm.is_active,
        });
        toast.success("Card added.");
      }
      setCardDialog(false);
      await load();
    } catch (e: unknown) {
      console.error(e);
      toast.error("Could not save card.");
    } finally {
      setSavingCard(false);
    }
  };

  const removeCard = async (c: SitePageCardRow) => {
    if (!window.confirm(`Remove "${c.name}"?`)) {
      return;
    }
    try {
      await adminService.deleteSitePageCard(c.id);
      toast.success("Card removed.");
      await load();
    } catch (e: unknown) {
      console.error(e);
      toast.error("Delete failed.");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex min-h-[40vh] items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          Loading…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-5xl space-y-8 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="mb-2 text-xl font-semibold tracking-tight md:text-3xl">
              {heading}
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage sections and contact cards for the public{" "}
              <code className="rounded bg-muted px-1 py-0.5">{pageSlug}</code>{" "}
              page. The page title below is the main heading visitors see.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void load()}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <Label htmlFor="page-title" required>
            Public page title
          </Label>
          <div className="mt-2 flex flex-wrap gap-2">
            <Input
              id="page-title"
              value={pageTitle}
              onChange={e => setPageTitle(e.target.value)}
              placeholder="e.g. Committee Members and Supporters"
              className="max-w-xl flex-1"
            />
            <Button
              type="button"
              disabled={savingTitle}
              onClick={() => void savePageTitle()}
            >
              {savingTitle ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Save title"
              )}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">Sections &amp; cards</h2>
          <Button type="button" onClick={openCreateSection}>
            <Plus className="mr-2 h-4 w-4" />
            Add section
          </Button>
        </div>

        <div className="space-y-6">
          {sections.length === 0 ? (
            <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
              No sections yet. Add a section, then add cards (name, email, role,
              address, etc.).
            </p>
          ) : (
            sections.map(sec => (
              <div
                key={sec.id}
                className="rounded-lg border bg-card p-4 shadow-sm"
              >
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="text-base font-semibold">{sec.title}</h3>
                    <p className="text-xs text-muted-foreground">
                      Order {sec.sort_order} · Grid:{" "}
                      {sec.grid_columns === 2 ? "2 columns" : "3 columns"} ·{" "}
                      {sec.is_active ? "Active" : "Hidden"}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openEditSection(sec)}
                    >
                      <Pencil className="mr-1 h-3.5 w-3.5" />
                      Edit section
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openCreateCard(sec.id)}
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      Add card
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => void removeSection(sec)}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                </div>

                <ul className="divide-y rounded-md border">
                  {(sec.cards ?? []).length === 0 ? (
                    <li className="px-3 py-4 text-sm text-muted-foreground">
                      No cards in this section.
                    </li>
                  ) : (
                    [...sec.cards]
                      .sort(
                        (a, b) => a.sort_order - b.sort_order || a.id - b.id
                      )
                      .map(c => (
                        <li
                          key={c.id}
                          className="flex flex-wrap items-center justify-between gap-2 px-3 py-3"
                        >
                          <div className="min-w-0">
                            <p className="font-medium">{c.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {[c.email, c.phone].filter(Boolean).join(" · ") ||
                                "—"}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditCard(c)}
                            >
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              onClick={() => void removeCard(c)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </li>
                      ))
                  )}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>

      <Dialog open={sectionDialog} onOpenChange={setSectionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSection ? "Edit section" : "New section"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="sec-title" required>
                Section title
              </Label>
              <Input
                id="sec-title"
                value={sectionForm.title}
                onChange={e =>
                  setSectionForm(f => ({ ...f, title: e.target.value }))
                }
                placeholder="e.g. Advisors"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="sec-order">Sort order</Label>
              <Input
                id="sec-order"
                type="number"
                min={0}
                value={sectionForm.sort_order}
                onChange={e =>
                  setSectionForm(f => ({
                    ...f,
                    sort_order: Number(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Grid columns</Label>
              <Select
                value={String(sectionForm.grid_columns)}
                onValueChange={v =>
                  setSectionForm(f => ({
                    ...f,
                    grid_columns: Number(v) === 2 ? 2 : 3,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 columns (wide cards)</SelectItem>
                  <SelectItem value="3">3 columns</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="sec-active"
                checked={sectionForm.is_active}
                onCheckedChange={v =>
                  setSectionForm(f => ({ ...f, is_active: v }))
                }
              />
              <Label htmlFor="sec-active">Active (visible on site)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSectionDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={savingSection}
              onClick={() => void saveSection()}
            >
              {savingSection ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cardDialog} onOpenChange={setCardDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCard ? "Edit card" : "New card"}</DialogTitle>
          </DialogHeader>
          <div className="grid max-h-[70vh] gap-3 overflow-y-auto py-2 pr-1">
            <div className="grid gap-2">
              <Label htmlFor="card-name" required>
                Name
              </Label>
              <Input
                id="card-name"
                value={cardForm.name}
                onChange={e =>
                  setCardForm(f => ({ ...f, name: e.target.value }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="card-email">Email</Label>
              <Input
                id="card-email"
                type="email"
                value={cardForm.email}
                onChange={e =>
                  setCardForm(f => ({ ...f, email: e.target.value }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="card-phone">Phone</Label>
              <Input
                id="card-phone"
                value={cardForm.phone}
                onChange={e =>
                  setCardForm(f => ({ ...f, phone: e.target.value }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="card-role">Role / title</Label>
              <Textarea
                id="card-role"
                rows={3}
                value={cardForm.role}
                onChange={e =>
                  setCardForm(f => ({ ...f, role: e.target.value }))
                }
                placeholder="e.g. President - North America"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="card-detail">Location / address</Label>
              <Textarea
                id="card-detail"
                rows={2}
                value={cardForm.detail_line}
                onChange={e =>
                  setCardForm(f => ({ ...f, detail_line: e.target.value }))
                }
                placeholder="City, region, or full address"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="card-order">Sort order</Label>
              <Input
                id="card-order"
                type="number"
                min={0}
                value={cardForm.sort_order}
                onChange={e =>
                  setCardForm(f => ({
                    ...f,
                    sort_order: Number(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="card-active"
                checked={cardForm.is_active}
                onCheckedChange={v =>
                  setCardForm(f => ({ ...f, is_active: v }))
                }
              />
              <Label htmlFor="card-active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCardDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={savingCard}
              onClick={() => void saveCard()}
            >
              {savingCard ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
